import { Location } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { Action, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { schedulesRoute } from 'src/app/app-routing.module';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { LoadPageSuccessProps } from 'src/app/common-components/register/models/register-actions';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { SchedulePrivileges } from '../models/schedule-privileges';
import { ScheduleOrderingOptions, ScheduleRegisterOptions } from '../models/schedule-register-options';
import { ScheduleRegisterListItem } from '../models/schedule-register-results.model';
import * as scheduleRegisterActions from '../state/schedule-register.actions';
import { selectQuickFilters } from '../state/schedule-register.reducer';
import * as scheduleRegisterSelectors from '../state/schedule-register.selectors';

@Component({
   selector: 'ndt-schedule-register-view',
   styleUrls: ['./schedule-register-view.component.scss'],
   templateUrl: './schedule-register-view.component.html',
   standalone: false,
})
export class ScheduleRegisterViewComponent
   extends EntityRegisterView<ScheduleRegisterListItem, ScheduleRegisterOptions, ScheduleOrderingOptions>
   implements OnDestroy, AfterViewInit
{
   protected selectQuickFilters(): (state: object) => ScheduleRegisterOptions | null {
      return selectQuickFilters;
   }
   @Output() selectedId = new EventEmitter<number>();

   @Output() deleteSchedule = new EventEmitter<number>();

   @Output() saveScheduleAs = new EventEmitter<ScheduleRegisterListItem>();

   @Input() schedulePrivileges: SchedulePrivileges;

   pluralName = 'Schedules';

   currentSelectedId: number;

   private destroyed$ = new Subject<void>();

   constructor(
      private twoColumnLayoutService: TwoColumnLayoutService,
      public store: Store,
      protected route: ActivatedRoute,
      private actions$: Actions,
      private location: Location,
      private router: Router
   ) {
      super(store);

      this.twoColumnLayoutService.selectedId$.pipe(takeUntil(this.destroyed$)).subscribe((selectedId) => {
         if (selectedId) {
            this.currentSelectedId = selectedId;
         }
      });
   }

   @Input()
   getRouterLink(id: number) {
      return `/${schedulesRoute}/${id}`;
   }

   ngOnDestroy(): void {
      this.twoColumnLayoutService.setSelectedId(null);
      this.destroyed$.next();
   }

   ngAfterViewInit(): void {
      let id = this.route.snapshot.params['id'];
      if (id) {
         if (isNaN(id)) {
            this.router.navigate(['/not-found'], { skipLocationChange: true });
            return;
         }
         id = +id;
         this.actions$
            .pipe(
               ofType(
                  scheduleRegisterActions.scheduleRegisterAdapterActions.loadSuccess,
                  scheduleRegisterActions.scheduleRegisterAdapterActions.loadPageSuccess
               ),
               takeUntil(this.destroyed$),
               takeUntil(this.actions$.pipe(ofType(scheduleRegisterActions.loadedUrlParams))),
               withLatestFrom(this.store.select(scheduleRegisterSelectors.selectEntities))
            )
            .subscribe((entities) => {
               this.findRowForUrlParamId(id, entities);
            });
      } else {
         this.subscribeToSelectedId();
      }
   }

   findRowForUrlParamId(
      id: number,
      entities: [LoadPageSuccessProps<ScheduleRegisterListItem> & Action<string>, Dictionary<ScheduleRegisterListItem>]
   ): void {
      setTimeout(() => {
         this.store.dispatch(scheduleRegisterActions.loadingUrlParams());
         const cell = document.querySelector(`#cell-${id}`);
         const row = cell?.closest('tr') as HTMLTableRowElement | null;

         if (row) {
            this.subscribeToSelectedId();
            this.store.dispatch(scheduleRegisterActions.loadedUrlParams({ scheduleId: id }));
            this.currentSelectedId = id;
            this.twoColumnLayoutService.setSelectedId(id);
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
         } else if (entities[0]?.hasNextPage) {
            this.store.dispatch(scheduleRegisterActions.scheduleRegisterAdapterActions.loadPage());
         } else {
            this.router.navigate(['/not-found'], { skipLocationChange: true });
            this.store.dispatch(scheduleRegisterActions.loadedUrlParams({ scheduleId: id }));
         }
      });
   }

   private subscribeToSelectedId(): void {
      this.twoColumnLayoutService.selectedId$.pipe(takeUntil(this.destroyed$)).subscribe((selectedId) => {
         if (selectedId) {
            const newUrl = `/schedules/${selectedId}`;
            this.location.replaceState(newUrl);
         }
      });
   }

   onScheduleClick(scheduleId: number) {
      this.selectedId.emit(scheduleId);
      this.currentSelectedId = scheduleId;
   }

   canDelete(): boolean {
      return this.schedulePrivileges.canDelete;
   }

   onDeleteClick(): void {
      if (this.currentSelectedId !== undefined) {
         this.twoColumnLayoutService.emitDeleteSchedule(this.currentSelectedId);
      }
   }

   onSaveAsClick(schedule: ScheduleRegisterListItem): void {
      this.saveScheduleAs.emit(schedule);
   }
}
